import React, { useEffect, useState } from "react";
import AxiosInstance from "../../../AxiosInstance.jsx";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useUserType } from "../../../../contexts/UserTypeContext.jsx";
import {
  Grid,
  Typography,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Paper,
  useTheme,
} from "@mui/material";
import SettingsCardHeader, {
  getSettingsCardShellSx,
} from "../../../../components/Setting/SettingsCardHeader.jsx";
import { Col } from "react-bootstrap";
import showToast from "../../../../components/Toast/Toster.jsx";
import BlueButton from "../../../../components/Button/BlueButton.jsx";
import WhiteButton from "../../../../components/Button/WhiteButton.jsx";
import { Person } from "@mui/icons-material";
import SettingsLayout from "../../../../components/Layouts/SettingsLayout.jsx";
import { getItem } from "../../../../utils/storage";
import { clearAuthCache } from "../../../../components/Login/Auth.jsx";

function NameFormat() {
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const theme = useTheme();
  const isDarkUi = theme.palette.mode === "dark";
  const primaryColor = isDonationMode ? "#68b2cb" : "#063164";
  const optionBorderIdle = isDarkUi ? "#3d4f66" : "#E5E7EB";
  const optionBorderHover = isDarkUi ? "#5a6b82" : "#D1D5DB";
  const optionPaperBg = isDarkUi ? "#252f3f" : "#FFFFFF";
  const optionPaperBgSelected = isDarkUi ? "#2e3d52" : "#f0f7ff";
  const titleInk = isDarkUi ? "#e8eaed" : "#1F2937";
  const descInk = isDarkUi ? "#b8c5d6" : "#6B7280";
  const capInk = isDarkUi ? "#9aa0a6" : "#9CA3AF";
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [originalFormat, setOriginalFormat] = useState(null);

  const handleFormatChange = (event) => {
    setSelectedFormat(event.target.value);
  };

  const fetchNameFormat = async () => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.get(`/v1/user/name-format/${CompanyId}`, {});
      if (response?.data?.statusCode === 200) {
        const format = response?.data?.data?.NameSortOrder;

        if (!format || format === "" || format === null) {
          setSelectedFormat("FirstNameToLastName");
          setOriginalFormat("FirstNameToLastName");
        } else {
          setSelectedFormat(format);
          setOriginalFormat(format);
        }
      } else {
        setSelectedFormat("FirstNameToLastName");
        setOriginalFormat("FirstNameToLastName");
      }
    } catch (error) {
      console.error("Error fetching name format:", error.message);
      setSelectedFormat("FirstNameToLastName");
      setOriginalFormat("FirstNameToLastName");
    }
  };

  const setNameFormat = async (NameSortOrder, showToastMessage = true) => {
    try {
      const CompanyId = getItem("CompanyId");
      const response = await AxiosInstance.put(`/v1/user/name-format/${CompanyId}`, {
        NameSortOrder,
      });

      if (response?.data?.statusCode === 200) {
        setOriginalFormat(NameSortOrder);
        clearAuthCache();
        if (showToastMessage) {
          showToast.success("Name format updated!", {
            position: "top-center",
            autoClose: 800,
          });
        }
      } else {
        if (showToastMessage) {
          showToast.error("Failed to update name format.", {
            position: "top-center",
            autoClose: 2000,
          });
        }
      }
    } catch (error) {
      console.error("Error updating name format:", error?.message);
      if (showToastMessage) {
        showToast.error("Error updating name format. Please try again.", {
          position: "top-center",
          autoClose: 2000,
        });
      }
    }
  };

  useEffect(() => {
    fetchNameFormat();
  }, []);

  // Check if there are any changes from the original format
  const hasChanges = () => {
    if (originalFormat === null) {
      return false;
    }
    return selectedFormat !== originalFormat;
  };

  // Check if the format is at default value
  const isAtDefaults = () => {
    const defaultFormat = "FirstNameToLastName";
    return selectedFormat === defaultFormat;
  };

  const formatOptions = [
    {
      value: "FirstNameToLastName",
      label: "First Name Last Name",
      example: "John Doe",
      description: "Standard Format",
    },
    {
      value: "LastNameToFirstName",
      label: "Last Name First Name",
      example: "Doe, John",
      description: "Reverse Format",
    },
  ];

  return (
    <>
      <Grid>
        <Grid className="">
          <SettingsLayout>
            <Col
              className="d-flex col-12 col-lg-12 col-md-12"
              style={{
                position: "relative",
                zIndex: "9",
                marginTop: "24px",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
              xl={12}
              lg={12}
              md={12}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  boxSizing: "border-box",
                  py: { xs: 1, sm: 1.5 },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Card elevation={0} sx={getSettingsCardShellSx(theme)}>
                  <SettingsCardHeader
                    title="Name Format Settings"
                    icon={Person}
                    centerIconWithTitle
                  />

                  <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Box sx={{ p: 0 }}>
                      {selectedFormat !== null && (
                      <RadioGroup
                        value={selectedFormat}
                        onChange={handleFormatChange}
                        sx={{ mb: { xs: 1.5, sm: 2 } }}
                      >
                        <Grid container spacing={2}>
                          {formatOptions.map((option) => {
                            const isSelected = selectedFormat === option.value;
                            return (
                              <Grid
                                key={option.value}
                                size={{
                                  xs: 12,
                                  md: 6
                                }}>
                                <Paper
                                  elevation={0}
                                  onClick={() => setSelectedFormat(option.value)}
                                  sx={{
                                    p: 2,
                                    borderRadius: "8px",
                                    border: "1px solid",
                                    borderColor: isSelected ? primaryColor : optionBorderIdle,
                                    bgcolor: isSelected ? optionPaperBgSelected : optionPaperBg,
                                    cursor: "pointer",
                                    transition: "border-color 0.15s ease, background-color 0.15s ease",
                                    "&:hover": {
                                      borderColor: isSelected
                                        ? primaryColor
                                        : optionBorderHover,
                                    },
                                  }}
                                >
                                  <FormControlLabel
                                    value={option.value}
                                    control={
                                      <Radio
                                        size="small"
                                        sx={{
                                          color: primaryColor,
                                          "&.Mui-checked": {
                                            color: primaryColor,
                                          },
                                          p: 0.5,
                                          alignSelf: "flex-start",
                                          mt: 0.25,
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{ pl: 0.5 }}>
                                        <Typography
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: "0.95rem",
                                            color: titleInk,
                                            mb: 0.25,
                                          }}
                                        >
                                          {option.label}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: descInk, lineHeight: 1.4 }}
                                        >
                                          {option.description}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: capInk,
                                            display: "block",
                                            mt: 1,
                                          }}
                                        >
                                          Example: {option.example}
                                        </Typography>
                                      </Box>
                                    }
                                    sx={{
                                      alignItems: "flex-start",
                                      m: 0,
                                      width: "100%",
                                      "& .MuiFormControlLabel-label": {
                                        width: "100%",
                                      },
                                    }}
                                  />
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </RadioGroup>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          gap: { xs: 1, sm: 2 },
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <WhiteButton
                          onClick={async () => {
                            try {
                              setSelectedFormat("FirstNameToLastName");
                              setOriginalFormat("FirstNameToLastName");
                              await setNameFormat("FirstNameToLastName", false);
                              showToast.success("Reset to default format!", {
                                position: "top-center",
                                autoClose: 1000,
                              });
                            } catch (error) {
                              console.error("Error resetting format:", error);
                              setSelectedFormat("FirstNameToLastName");
                              setOriginalFormat("FirstNameToLastName");
                              showToast.error(
                                "Error resetting format.",
                                {
                                  position: "top-center",
                                  autoClose: 2000,
                                }
                              );
                            }
                          }}
                          variant="outline"
                          type="button"
                          disabled={isAtDefaults()}
                          isDonationMode={isDonationMode}
                          label={
                            <>
                              <RestartAltIcon sx={{ mr: 1, fontSize: 20 }} />{" "}
                              Reset
                            </>
                          }
                        />
                        <BlueButton
                          id="saveButton"
                          type="submit"
                          disabled={!hasChanges()}
                          Typography="Save Changes"
                          isDonationMode={isDonationMode}
                          onClick={async () => {
                            if (!selectedFormat || !hasChanges()) {
                              return;
                            }
                            await setNameFormat(selectedFormat);
                          }}
                          label={"Save Changes"}
                        />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Col>
          </SettingsLayout>
        </Grid>
      </Grid>
    </>
  );
}

export default NameFormat;

